/**
 * Ride status pieces — `DispatchStageMessage` + `RideStatusPanel`
 * (iter-79 decomposition slice 3 of Passenger.jsx).
 *
 * Strict isolation contract (matches Driver.jsx / Slice 1-2 playbook):
 *   - Zero behavioural change vs. the original inline definitions.
 *   - Co-located helpers (`statusMap`, `phaseMap`, `pillFor`) are re-exported
 *     so the remaining consumers in Passenger.jsx (`ActiveRideDetail`,
 *     `HistoryItem`) keep working with a single-line import change.
 *   - No dispatch / state-machine / janitor / offer-routing logic touched.
 */
import React from "react";
import { X, Phone, MessageSquare } from "lucide-react";
import { Button } from "../ui/button";
import { toast } from "sonner";
import { api } from "../../lib/api";

export const statusMap = {
  searching: { label: "FINDING DRIVER", color: "#F59E0B", pulse: true },
  offered: { label: "OFFERED TO DRIVER", color: "#F59E0B", pulse: true },
  accepted: { label: "DRIVER EN ROUTE", color: "#10B981", pulse: true },
  in_progress: { label: "IN TRANSIT", color: "#002FA7", pulse: true },
  completed: { label: "COMPLETED", color: "#10B981", pulse: false },
  cancelled: { label: "CANCELLED", color: "#9CA3AF", pulse: false },
  scheduled: { label: "SCHEDULED", color: "#002FA7", pulse: false },
};

// Phase-truth pill — when `ride.phase` is set, it takes precedence over
// statusMap so the rider sees the same canonical state the backend tracks.
// Mirrors the Driver app's iteration-26 refactor.
export const phaseMap = {
  scheduled:              { label: "SCHEDULED", color: "#002FA7", pulse: false },
  dispatching:            { label: "FINDING DRIVER", color: "#F59E0B", pulse: true },
  offered:                { label: "OFFERED TO DRIVER", color: "#F59E0B", pulse: true },
  assigned:               { label: "DRIVER EN ROUTE", color: "#10B981", pulse: true },
  driver_en_route:        { label: "DRIVER EN ROUTE", color: "#10B981", pulse: true },
  driver_arrived_pickup:  { label: "DRIVER HAS ARRIVED", color: "#10B981", pulse: true },
  trip_in_progress:       { label: "IN TRANSIT", color: "#002FA7", pulse: true },
  driver_arrived_dropoff: { label: "ARRIVING NOW", color: "#002FA7", pulse: true },
  completed:              { label: "COMPLETED", color: "#10B981", pulse: false },
  cancelled:              { label: "CANCELLED", color: "#9CA3AF", pulse: false },
};

export const pillFor = (ride) => (ride.phase && phaseMap[ride.phase]) ||
  statusMap[ride.status] ||
  { label: ride.status, color: "#0A0A0A" };

/** Live dispatch-stage explainer shown while the passenger is waiting for a driver. */
export const DispatchStageMessage = ({ ride, dispatchStatus, compact = false }) => {
  if (!ride || !["searching", "offered"].includes(ride.status)) return null;
  // Wait for the first poll to land so we never flash a placeholder
  // "Finding a driver…" if the truthful stage is actually "no_drivers_online".
  if (!dispatchStatus) return null;
  const stage = dispatchStatus?.stage;
  const onlineCount = dispatchStatus?.online_driver_count ?? null;

  let tone = "info"; // info | warn | danger
  let title = "Finding you a driver…";
  let detail = "Hold tight — we're matching you with the nearest available driver.";

  if (stage === "no_drivers_online") {
    tone = "danger";
    title = "No drivers are online yet";
    detail = "We'll keep searching and assign one as soon as someone goes online.";
  } else if (stage === "no_eligible_drivers") {
    tone = "warn";
    title = "Every nearby driver is busy";
    detail = `Hang on — ${onlineCount ?? 0} driver(s) online but none are free right now. We'll retry automatically.`;
  } else if (stage === "offered") {
    tone = "info";
    title = dispatchStatus?.offered_to_name ? `Offer sent to ${dispatchStatus.offered_to_name}` : "Offer sent to a driver";
    detail = "Waiting for them to accept — this usually takes a few seconds.";
  } else if (stage === "searching") {
    tone = "info";
    title = "Finding you a driver…";
    detail = onlineCount != null ? `${onlineCount} driver(s) online nearby.` : "Hold tight while we route a driver to you.";
  }

  const palette = {
    info:   { border: "#FCD34D", bg: "#FFFBEB", text: "#92400E", dot: "#F59E0B" },
    warn:   { border: "#FB923C", bg: "#FFF7ED", text: "#9A3412", dot: "#F97316" },
    danger: { border: "#EF4444", bg: "#FEF2F2", text: "#991B1B", dot: "#EF4444" },
  }[tone];

  if (compact) {
    return (
      <div
        className="mt-2 text-xs flex items-center gap-2"
        style={{ color: palette.text }}
        data-testid="dispatch-stage-compact"
        data-stage={stage || "loading"}
      >
        <span className="w-1.5 h-1.5 rounded-full inline-block" style={{ backgroundColor: palette.dot }} />
        <span className="font-semibold">{title}</span>
      </div>
    );
  }
  return (
    <div
      className="border-2 p-3 flex items-start gap-3"
      style={{ borderColor: palette.border, backgroundColor: palette.bg }}
      data-testid="dispatch-stage-banner"
      data-stage={stage || "loading"}
    >
      <span
        className="w-2.5 h-2.5 rounded-full mt-1.5 flex-shrink-0 pulse-warning"
        style={{ backgroundColor: palette.dot }}
      />
      <div className="flex-1 min-w-0">
        <div className="font-heading font-black text-sm leading-tight" style={{ color: palette.text }}>
          {title}
        </div>
        <div className="text-xs mt-0.5" style={{ color: palette.text, opacity: 0.85 }}>
          {detail}
        </div>
      </div>
    </div>
  );
};

export const RideStatusPanel = ({ ride, dispatchStatus, onCancel, onMessage }) => {
  const s = pillFor(ride);
  // Slice 2 — masked voice call to the driver (mirrors ActiveRideDetail).
  const callDriver = async () => {
    try {
      const res = await api.get(`/passenger/rides/${ride.id}/call`);
      const data = res?.data || {};
      if (data.available && data.call_number) {
        toast.success("Connecting through Crystal Dispatch…", { description: data.note });
        window.location.href = `tel:${data.call_number}`;
      } else {
        toast.message(data.reason || "Calling isn't available right now.");
      }
    } catch (e) {
      toast.error("Couldn't start the call. Please try again.");
    }
  };
  return (
    <div data-testid="ride-status-panel">
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <span className={`w-2.5 h-2.5 rounded-full ${s.pulse ? "pulse-warning" : ""}`} style={{ backgroundColor: s.color }} />
          <span className="label-mini">{s.label}</span>
        </div>
        {ride.status !== "in_progress" && ride.status !== "completed" && (
          <button onClick={onCancel} className="text-xs text-muted-foreground hover:text-destructive flex items-center gap-1" data-testid="cancel-ride-btn">
            <X className="w-3 h-3" /> Cancel
          </button>
        )}
      </div>
      <div className="text-sm font-semibold">{ride.pickup_address} → {ride.dropoff_address}</div>
      <DispatchStageMessage ride={ride} dispatchStatus={dispatchStatus} compact />
      {ride.driver && (
        <div className="mt-2 text-xs text-muted-foreground">
          {ride.driver.name} · {ride.driver.vehicle_plate} · {ride.driver.vehicle_make}
        </div>
      )}
      {/* Contact row — visible directly on the prominent floating map panel
          so mobile passengers can reach the chat without scrolling past
          the whole map. Mirrors the row in ActiveRideDetail (below the
          fold on mobile). Phase 1: Call is a stub, Message opens the
          shared RideChatDialog. Only renders once a driver is assigned
          (ride.driver_id set by /driver/accept) — matches the spec:
          show when driver assigned / arrived / trip in progress; hide
          when searching / offered / completed / cancelled. */}
      {ride.driver_id && ride.status !== "completed" && ride.status !== "cancelled" && (
        <div className="mt-3 flex gap-2" data-testid="ride-contact-row-map">
          <Button
            variant="outline"
            size="sm"
            onClick={callDriver}
            className="text-xs h-8 flex-1"
            data-testid="ride-call-btn-map"
          >
            <Phone className="w-3 h-3 mr-1.5" /> CALL
          </Button>
          <Button
            variant="default"
            size="sm"
            onClick={onMessage}
            className="text-xs h-8 flex-1 bg-[#0A0A0A] hover:bg-[#0A0A0A]/90"
            data-testid="ride-message-btn-map"
          >
            <MessageSquare className="w-3 h-3 mr-1.5" /> MESSAGE
          </Button>
        </div>
      )}
    </div>
  );
};
